import { ChecklistModuleDefinition } from '../../types/due-diligence';

export const multipleParcelsModule: ChecklistModuleDefinition = {
  code: 'multiple_parcels',
  name: 'Multiple Parcels',
  description: 'Multi-parcel property considerations',
  categories: [
    {
      code: 'parcel_inventory',
      name: 'Parcel Inventory',
      items: [
        {
          code: 'parcel_list',
          title: 'Parcel inventory',
          description: 'Document all parcels in sale',
          isGroupTask: false,
          dataFields: [
            { key: 'total_parcels', label: 'Total Parcels', type: 'number' },
            { key: 'total_acres', label: 'Total Acres', type: 'number', unit: 'acres' },
            { key: 'parcel_numbers', label: 'Parcel Numbers', type: 'array' },
          ],
        },
        {
          code: 'parcel_details',
          title: 'Individual parcel details',
          description: 'Details for each parcel',
          isGroupTask: false,
          dataFields: [
            { key: 'parcels', label: 'Parcel Details', type: 'array' },
          ],
        },
      ],
    },
    {
      code: 'parcel_relationships',
      name: 'Parcel Relationships',
      items: [
        {
          code: 'contiguity',
          title: 'Contiguity assessment',
          description: 'Are parcels connected?',
          isGroupTask: false,
          dataFields: [
            { key: 'all_contiguous', label: 'All Parcels Contiguous', type: 'boolean' },
            { key: 'non_contiguous_parcels', label: 'Non-Contiguous Parcels', type: 'array' },
            { key: 'access_between', label: 'Access Between Parcels', type: 'string' },
          ],
          suggestedFlags: [
            { flag: 'yellow', condition: 'Non-contiguous parcels' },
            { flag: 'red', condition: 'Isolated parcels with no access' },
          ],
        },
        {
          code: 'cross_parcel_access',
          title: 'Cross-parcel access',
          description: 'Access rights between parcels',
          isGroupTask: false,
          dataFields: [
            { key: 'easements_in_place', label: 'Easements in Place', type: 'boolean' },
            { key: 'road_crossings', label: 'Road Crossings Needed', type: 'boolean' },
            { key: 'access_issues', label: 'Access Issues', type: 'string' },
          ],
        },
        {
          code: 'shared_resources',
          title: 'Shared resources',
          description: 'Resources shared across parcels',
          isGroupTask: false,
          dataFields: [
            { key: 'shared_water', label: 'Shared Water System', type: 'boolean' },
            { key: 'shared_electric', label: 'Shared Electric', type: 'boolean' },
            { key: 'shared_septic', label: 'Shared Septic', type: 'boolean' },
            { key: 'shared_access', label: 'Shared Access Road', type: 'boolean' },
          ],
          suggestedFlags: [
            { flag: 'yellow', condition: 'Shared utilities across parcels' },
          ],
        },
      ],
    },
    {
      code: 'zoning_tax',
      name: 'Zoning & Tax by Parcel',
      items: [
        {
          code: 'zoning_by_parcel',
          title: 'Zoning by parcel',
          description: 'Zoning for each parcel',
          isGroupTask: false,
          dataFields: [
            { key: 'same_zoning', label: 'Same Zoning All Parcels', type: 'boolean' },
            { key: 'zoning_summary', label: 'Zoning Summary', type: 'string' },
            { key: 'zoning_conflicts', label: 'Zoning Conflicts', type: 'array' },
          ],
          suggestedFlags: [
            { flag: 'yellow', condition: 'Different zoning between parcels' },
          ],
        },
        {
          code: 'tax_by_parcel',
          title: 'Tax assessment by parcel',
          description: 'Tax details per parcel',
          isGroupTask: false,
          dataFields: [
            { key: 'total_annual_tax', label: 'Total Annual Tax', type: 'number', unit: 'dollars' },
            { key: 'tax_breakdown', label: 'Tax Breakdown by Parcel', type: 'string' },
            { key: 'different_tax_rates', label: 'Different Tax Rates', type: 'boolean' },
          ],
        },
        {
          code: 'ag_exemption_by_parcel',
          title: 'Ag exemption by parcel',
          description: 'Ag tax status per parcel',
          isGroupTask: false,
          dataFields: [
            { key: 'all_ag_exempt', label: 'All Parcels Ag Exempt', type: 'boolean' },
            { key: 'exemption_details', label: 'Exemption Details', type: 'string' },
            { key: 'rollback_by_parcel', label: 'Rollback by Parcel', type: 'string' },
          ],
        },
      ],
    },
    {
      code: 'subdivision_potential',
      name: 'Subdivision Potential',
      items: [
        {
          code: 'combination_potential',
          title: 'Combination potential',
          description: 'Can parcels be legally combined?',
          isGroupTask: false,
          dataFields: [
            { key: 'combination_possible', label: 'Combination Possible', type: 'boolean' },
            { key: 'combination_benefits', label: 'Combination Benefits', type: 'string' },
            { key: 'combination_process', label: 'Combination Process', type: 'string' },
          ],
        },
        {
          code: 'separation_potential',
          title: 'Separation/split potential',
          description: 'Can parcels be sold separately?',
          isGroupTask: false,
          dataFields: [
            { key: 'can_sell_separately', label: 'Can Sell Separately', type: 'boolean' },
            { key: 'exit_strategy', label: 'Exit Strategy Flexibility', type: 'string' },
            { key: 'value_if_split', label: 'Value if Split', type: 'number', unit: 'dollars' },
          ],
          suggestedFlags: [
            { flag: 'big_green', condition: 'Parcels sellable separately at profit' },
          ],
        },
        {
          code: 'future_subdivision',
          title: 'Future subdivision potential',
          description: 'Could parcels be further subdivided?',
          isGroupTask: false,
          dataFields: [
            { key: 'subdivision_allowed', label: 'Subdivision Allowed', type: 'boolean' },
            { key: 'min_lot_size', label: 'Min Lot Size', type: 'number', unit: 'acres' },
            { key: 'potential_lots', label: 'Potential Lots', type: 'number' },
            { key: 'subdivision_costs', label: 'Est. Subdivision Costs', type: 'number', unit: 'dollars' },
          ],
          suggestedFlags: [
            { flag: 'big_green', condition: 'Strong subdivision potential' },
          ],
        },
      ],
    },
    {
      code: 'title_issues',
      name: 'Multi-Parcel Title Issues',
      items: [
        {
          code: 'title_by_parcel',
          title: 'Title status by parcel',
          description: 'Title issues per parcel',
          isGroupTask: false,
          dataFields: [
            { key: 'all_clear_title', label: 'All Clear Title', type: 'boolean' },
            { key: 'issues_by_parcel', label: 'Issues by Parcel', type: 'string' },
          ],
        },
        {
          code: 'ownership_history',
          title: 'Ownership history',
          description: 'How parcels came together',
          isGroupTask: false,
          dataFields: [
            { key: 'assembled_by_seller', label: 'Assembled by Seller', type: 'boolean' },
            { key: 'family_land', label: 'Family Land', type: 'boolean' },
            { key: 'ownership_notes', label: 'Ownership Notes', type: 'string' },
          ],
        },
      ],
    },
  ],
};

